import { errorSwal, successSwal, toastSwal, warningSwal } from '@/helper/swals';
import api from '@/services/Api';
import {
    ExtendedPatientState,
    ImpfPrintPayload,
    PatImpfungenFilter,
    PatientimpfungenState,
    RootState,
} from '@/store/types';
import Impferinnerung from '@/types/Impferinnerung';
import Impfung from '@/types/Impfung';
import Karteieintrag from '@/types/Karteieintrag';
import PatUntersuchung from '@/types/PatUntersuchung';
import { Satzart } from '@/types/Satzart';
import { AxiosError, AxiosResponse } from 'axios';
import { ActionContext, Module } from 'vuex';

type PatientenGetters = {
    getNrOffeneImpfungen: number;
    getOffeneUntersuchungen: PatUntersuchung[];
    getUntersuchungen: PatUntersuchung[];
    getNrUntersuchungen: number;
    untersuchungen: PatUntersuchung[];
    untersuchungenEintraege: Karteieintrag[];
};

type Context = ActionContext<PatientimpfungenState, RootState>;

export const patientimpfungen: Module<PatientimpfungenState, RootState> = {
    namespaced: true,
    state: {
        patientImpfungNew: new Impfung(),
        impfungen: [],
        erinnerungen: [],
        filter: {
            satzarten: Satzart.values(),
            elga: false,
            gruppierung: false,
        },
        dt_load: false,
        loadingText: false,
        selected_impfeintrag: new Karteieintrag(),
        gruppiert: false,
    },
    actions: {
        impfungNew({ commit }: Context, payload: boolean): void {
            commit('impfungNew', payload);
        },
        loadImpfungen({ commit, state }: Context): void {
            const patnr = (state as ExtendedPatientState).patientData?.patnr;
            if (!patnr) {
                return;
            }

            commit('setDt_load', true);

            const gruppierung = state.filter.gruppierung;

            api.post('ivs/patimpfungen/load', { patnr, filter: state.filter })
                .then((response: AxiosResponse) => {
                    if (patnr === (state as ExtendedPatientState).patientData?.patnr) {
                        commit('setImpfungen', response.data.body);
                        commit('setGruppiert', gruppierung);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                })
                .finally(() => commit('setDt_load', false));
        },
        createImpfung({ commit, dispatch, rootState }: Context, impfung: Impfung): void {
            commit('setLoadersImpfung', true);

            api.post('ivs/patimpfungen/create', impfung)
                .then((response: AxiosResponse) => {
                    commit('setNeueImpfung', false);
                    commit('setDialogImpfungNachtrag', false);
                    dispatch('loadImpfungen');
                    dispatch('loadErinnerungen');
                    dispatch('getReiseplaene');

                    const { patimpfung_id, nachtrag, impfdatum, impfstoff_code } = response.data.body as Impfung;
                    const { impfstelle_id, user } = rootState.authentication;

                    if (nachtrag !== 'j' &&
                        impfstoff_code &&
                        user?.elga_access &&
                        (impfdatum ?? '').slice(0, 10) >= (user?.release_date ?? '')) {
                        api.post('ivs/impfpass/impfunguebertragen', { patimpfung_id, impfstelle_id })
                            .then((response: AxiosResponse) => {
                                console.log('createImpfung:', response.data);

                                if (!response.data?.body?.errors) {
                                    dispatch('loadImpfungen');
                                }

                                successSwal('e-Impfpass', 'Impfung übertragen');
                            })
                            .catch((error: AxiosError) => {
                                toastSwal('error', 'e-Impfpass', 'Übertragung fehlgeschlagen');
                                console.error(error);
                            });
                    }

                    successSwal('Impfung', 'gespeichert');
                })
                .catch((error: AxiosError<{ body?: unknown }>) => {
                    errorSwal('Fehler', 'Fehler beim Speichern der Impfung. (' + (error.response?.data?.body ?? error.response?.status) + ')');
                    console.error(error);
                })
                .finally(() => commit('setLoadersImpfung', false));
        },
        createImpfungUndNeu({ commit, dispatch, rootState }: Context, { impfung, nachtrag }): void {
            commit('setLoadersImpfungUndNeu', true);

            api.post('ivs/patimpfungen/create', impfung)
                .then((response: AxiosResponse) => {
                    commit('setNeueImpfung', false);
                    commit('setDialogImpfungNachtrag', false);
                    dispatch('loadImpfungen');
                    dispatch('loadErinnerungen');
                    dispatch('getReiseplaene');

                    const { patimpfung_id, nachtrag, impfdatum, impfstoff_code } = response.data.body as Impfung;
                    const { impfstelle_id, user } = rootState.authentication;

                    if (nachtrag !== 'j' &&
                        impfstoff_code &&
                        user?.elga_access &&
                        (impfdatum ?? '').slice(0, 10) >= (user?.release_date ?? '')) {
                        api.post('ivs/impfpass/impfunguebertragen', { patimpfung_id, impfstelle_id })
                            .then((response: AxiosResponse) => {
                                console.log('createImpfung:', response.data);

                                if (!response.data?.body?.errors) {
                                    dispatch('loadImpfungen');
                                }

                                successSwal('e-Impfpass', 'Impfung übertragen');
                            })
                            .catch((error: AxiosError) => {
                                toastSwal('error', 'e-Impfpass', 'Übertragung fehlgeschlagen');
                                console.error(error);
                            });
                    }

                    successSwal('Impfung', 'gespeichert');
                })
                .catch((error: AxiosError<{ body?: unknown }>) => {
                    errorSwal('Fehler', 'Fehler beim Speichern der Impfung. (' + (error.response?.data?.body ?? error.response?.status) + ')');
                    console.error(error);
                })
                .finally(() => {
                    commit('setLoadersImpfungUndNeu', false);
                    if (nachtrag) {
                        commit('setDialogImpfungNachtrag', true);
                    } else {
                        commit('setNeueImpfung', true);
                    }
                });
        },
        editImpfung({ commit, dispatch, rootState }: Context, impfung: Impfung): Promise<boolean> {
            commit('setLoadersImpfung', true);

            const { impfstelle_id } = rootState.authentication;
            const arbeitsplatz_id = rootState.session.arbeitsplatz;

            return api.post('ivs/patimpfungen/edit', { ...impfung, impfstelle_id, arbeitsplatz_id })
                .then((response: AxiosResponse) => {
                    commit('setLoadersImpfung', false);

                    if (typeof response.data.body !== 'string') {
                        successSwal('Impfung', 'gespeichert');
                        commit('setSelectedImpfeintrag', response.data?.body?.impfung);
                        dispatch('loadImpfungen');
                        dispatch('loadErinnerungen');
                        return true;
                    } else {
                        warningSwal('Fehler', response.data.body?.toString());
                        return false;
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    commit('setLoadersImpfung', false);
                    return false;
                });
        },
        setDurchgefuehrt({ commit, dispatch, rootState }: Context, payload): void {
            console.log(payload);
            api.post('/ivs/patplaene/durchgefuehrt',
            {
                art: payload.art,
                patplan_id: payload.patplan_id,
                patplanimpfstoff_id: payload.patplanimpfstoff_id,
                patuntersuchung_id: payload.patuntersuchung_id,
            })
                .then((response: AxiosResponse) => {
                    dispatch('getReiseplaene');
                    successSwal('Pläne', 'durchgeführt');
                })
                .catch(console.error);
        },
        resetDurchgefuehrt({ commit, dispatch, rootState }: Context, payload): void {
            api.post('/ivs/patplaene/resetdurchgefuehrt',
                {
                    art: payload.art,
                    patplan_id: payload.patplan_id,
                    patplanimpfstoff_id: payload.patplanimpfstoff_id,
                    patuntersuchung_id: payload.patuntersuchung_id,
                })
                .then((response: AxiosResponse) => {
                    dispatch('getReiseplaene');
                    successSwal('Pläne', 'zurückgesetzt');
                })
                .catch(console.error);
        },
        deleteImpfung({ commit, state, dispatch, rootState }: Context): void {
            const { patimpfung_id, eimpf_id } = state.selected_impfeintrag;
            const { impfstelle_id } = rootState.authentication;

            const deleteImpfung = (): void => {
                api.post('ivs/patimpfungen/deleteimpfung', { patimpfung_id })
                    .then(() => {
                        commit('setNeueImpfung', false);
                        commit('setDialogImpfungNachtrag', false);
                        commit('setDialogImpfeintragEdit', false);
                        dispatch('loadImpfungen');
                        dispatch('getReiseplaene');
                        successSwal('Impfung', 'gelöscht');
                    })
                    .catch(console.error)
                    .finally(() => commit('setLoadersDelete', false));
            };

            if (eimpf_id) {
                api.post('ivs/impfpass/impfungstornieren', { patimpfung_id, impfstelle_id })
                    .then((response: AxiosResponse) => {
                        console.log(response.data);
                        successSwal('e-Impfpass', 'Impfung storniert');
                        deleteImpfung();
                    })
                    .catch((error: AxiosError) => {
                        toastSwal('error', 'e-Impfpass', 'Stornierung fehlgeschlagen');
                        console.error(error);
                    });

                setTimeout(() => {
                    commit('setNeueImpfung', false);
                    commit('setDialogImpfungNachtrag', false);
                    commit('setDialogImpfeintragEdit', false);
                    dispatch('loadImpfungen');
                }, 300);
            } else {
                commit('setLoadersDelete', true);
                deleteImpfung();
            }
        },
        impfungUebertragen({ commit, dispatch, rootState }: Context,
                           payload: { patimpfung_id: number, impfstelle_id: number }): void {
            commit('setLoadersElga', { uebertragung: true });

            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/impfunguebertragen', { ...payload, system: true, arbeitsplatz_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    console.log('impfungUebertragen:', response.data);
                    if (!response.data?.body?.errors) {
                        successSwal('e-Impfpass', 'Impfung übertragen');
                    }
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error('impfungUebertragen:', error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => {
                    dispatch('loadImpfungen');
                    commit('setLoadersElga', { uebertragung: false });
                });
        },
        impfungStornieren({ commit, dispatch }: Context,
                          payload: { patimpfung_id: number, impfstelle_id: number }): void {
            commit('setLoadersElga', { stornierung: true });

            api.post('ivs/impfpass/impfungstornieren', { ...payload, system: true })
                .then((response: AxiosResponse) => {
                    console.log('impfungStornieren:', response.data);
                    dispatch('loadImpfungen');
                    successSwal('e-Impfpass', 'Impfung storniert');
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error('impfungStornieren:', error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => {
                    dispatch('loadImpfungen');
                    commit('setLoadersElga', { stornierung: false });
                });
        },
        loadErinnerungen({ commit, state }: Context): void {
            const patnr = (state as ExtendedPatientState).patientData?.patnr;
            if (!patnr) {
                return;
            }

            commit('setLoadersErinnerungen', true);

            api.post('impferinnerungen/impferinnerungen/load', { filter: { patnr }})
                .then((response) => commit('setErinnerungen', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersErinnerungen', false));
        },
        printImpfetikett({ commit, dispatch }: Context, payload: ImpfPrintPayload): void {
            commit('setLoaders', { impfetiketten: true });
            const data = {
                patImfpungId: payload.data,
                paperName: payload.paperName,
                teilimpfungsKuerzel: payload.teilimpfungsKuerzel
            };

            api.post('ivs/impfetiketten', data, { responseType: 'blob' })
                .then((response) => dispatch('globals/printImpfetiketten', { ...payload, data: response.data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoaders', { impfetiketten: false }));
        },
    },
    mutations: {
        impfungNew(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.impfungNew = visible;
        },
        setLoadersDelete(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).loaders.delete = visible;
        },
        setNeueImpfung(state: PatientimpfungenState, visible: boolean): void {
            if ((state as ExtendedPatientState).patientData) {
                state.patientImpfungNew = new Impfung();
                (state as ExtendedPatientState).dialogs.impfungNew = visible;
            }
        },
        setImpfungen(state: PatientimpfungenState, payload: Partial<Karteieintrag>[]): void {
            if ((state as ExtendedPatientState).patientData) {
                state.impfungen = payload.map((data) => new Karteieintrag(data));
            }
        },
        setErinnerungen(state: PatientimpfungenState, payload: Partial<Impferinnerung>[]): void {
            if ((state as ExtendedPatientState).patientData) {
                state.erinnerungen = payload.map((data) => new Impferinnerung(data));
            }
        },
        setFilters(state: PatientimpfungenState, filter: PatImpfungenFilter): void {
            state.filter = filter;
        },
        setDt_load(state: PatientimpfungenState, visible: boolean): void {
            state.dt_load = visible;
        },
        setSelectedImpfeintrag(state: PatientimpfungenState, payload: Karteieintrag): void {
            state.selected_impfeintrag = new Karteieintrag(payload);
        },
        setSelectedLeistungseintrag(state: PatientimpfungenState, payload: Karteieintrag): void {
            (state as ExtendedPatientState).selected_leistungseintrag = new Karteieintrag(payload);
        },
        setSelectedBrief(state: PatientimpfungenState, payload: Karteieintrag): void {
            (state as ExtendedPatientState).selected_brief = new Karteieintrag(payload);
        },
        setDialogImpfeintragEdit(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.impfungEdit = visible;
            if (!visible) {
                (state as ExtendedPatientState).selected_offeneImpfung = null;
            }
        },
        setLoadersImpfung(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).loaders.impfung = visible;
        },
        setLoadersImpfungUndNeu(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).loaders.impfungundneu = visible;
        },
        setLoadersErinnerungen(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).loaders.erinnerungen = visible;
        },
        setDialogImpfungNachtrag(state: PatientimpfungenState, visible: boolean): void {
            (state as ExtendedPatientState).dialogs.impfungNachtrag = visible;
        },
        setGruppiert(state: PatientimpfungenState, gruppiert: boolean): void {
            state.gruppiert = gruppiert;
        },
    },
    getters: {
        getNrOffeneImpfungenUndUntersuchungen: (state: PatientimpfungenState, getters: PatientenGetters): number => {
            return getters.getNrOffeneImpfungen + getters.getOffeneUntersuchungen.length;
        },
        getNrOffeneImpfungen: (state: PatientimpfungenState): number => {
            return (state as ExtendedPatientState).reiseplanimpfungen
                .filter((r) => r.geimpft !== 'j').length;
        },
        getUntersuchungen: (state: PatientimpfungenState): PatUntersuchung[] => {
            return (state as ExtendedPatientState).untersuchungen.filter((untersuchung) => !untersuchung.isGeplant);
        },
        getNrUntersuchungen: (state: PatientimpfungenState, getters: PatientenGetters): number => {
            return getters.getUntersuchungen.length;
        },
        getNrErinnerungen: (state: PatientimpfungenState): number => {
            return state.erinnerungen.filter(({ erinnert }) => erinnert !== 'j').length;
        },
        untersuchungenEintraege: (state: PatientimpfungenState): Karteieintrag[] => {
            return state.impfungen
                .filter(({ isUntersuchung }) => isUntersuchung);
        },
        untersuchungen: (state: PatientimpfungenState, getters: PatientenGetters): PatUntersuchung[] => {
            return getters.untersuchungenEintraege
                .map(({ untersuchung }: Karteieintrag) => untersuchung as PatUntersuchung);
        },
    },
};
